'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { ExternalLink, Play } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@kiao-voice/ui-kit'
import type { SiteDemoReady } from '@kiao-voice/api-contract/site-demo'
import { createYouTubePlayer, loadYouTubeApi, type YouTubePlayer } from '@/lib/youtube-iframe-api'
import { SUBTITLE_CUES, VIDEO_POSTER, YOUTUBE_ID, type SubtitleCue } from './subtitle-demo-cues'
import {
  cueIndexAt,
  formatCueClock,
  groupCuesBySpeaker,
  youtubeUrlAt,
  type SpeakerGroup,
} from './subtitle-demo-model'
import { useDemoStarted } from '@/lib/analytics/use-demo-started'

/**
 * 後端的 segment 轉成畫面要的 cue。只取起點：清單與跟播都認「最後一個已經開始的
 * 句子」，`end` 用不到（見 `cueIndexAt`）。
 *
 * 起點**保留小數**。後端的秒數本來就是浮點，在這裡取整等於把 seek 的目標推開幾十
 * 毫秒到將近一秒，影片一開口就對不上高亮的那一句。取整只發生在最後一哩：畫面上的
 * 時間碼與退路連結的秒數（都往下取整），以及 YouTube 的 `start`（只吃整數）。
 */
function toCues(demo: SiteDemoReady): SubtitleCue[] {
  return demo.segments.map((segment) => ({
    start: Math.max(0, segment.start),
    speaker: segment.speaker,
    text: segment.text,
  }))
}

/**
 * YouTube 的封面。用 `hqdefault` 而不是 `maxresdefault`：後者只有上傳者提供高解
 * 析縮圖時才存在，缺的時候 YouTube 回的是一張 120×90 的灰底佔位圖而不是 404，
 * 所以 `onError` 換圖那條路根本不會觸發，畫面會留一張糊掉的小圖。`hqdefault`
 * 每支影片都有，480×360 放在這個 16:9 的框裡也夠用。
 */
function youtubePoster(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
}

/**
 * 每一句按鈕的 aria-label。講者名寫在這裡而不是靠上面那列分隔標，因為分隔標對
 * 螢幕閱讀器是隱藏的——沒有講者的逐字稿就只報時間與內容。
 */
function cueLabel(cue: SubtitleCue): string {
  const time = formatCueClock(cue.start)
  return cue.speaker === null ? `${time}，${cue.text}` : `${time}，${cue.speaker}：${cue.text}`
}

/**
 * 讀播放器時間的節奏。250ms 是「跟得上影片」與「不要每秒重畫四次」的折衷，
 * 與會議示範、試轉進度條的畫面時鐘同一個數字。
 */
const PLAYBACK_TICK_MS = 250

/**
 * `onStateChange` 給的播放狀態碼。只認這三個：在播才需要讀時鐘，暫停或播完就停。
 * buffering 與 cued 不動：buffering 幾百毫秒就回到 playing，停了再起只是讓計時器
 * 多拆裝一次。
 */
const PLAYER_STATE = { ended: 0, playing: 1, paused: 2 } as const

/**
 * 點了一句之後，最多等幾個 tick 讓播放器走到那一句。
 *
 * `seekTo()` 不是瞬間的：接下來一兩次 `getCurrentTime()` 讀到的還是舊的位置，照算
 * 會把高亮閃回剛才那一句、250ms 後才跳回來。但等待要有期限：讀者可能點完之後又
 * 自己拖了進度條，那時新的位置才是對的，凍在那一句反而變成另一個錯。
 * 4 個 tick ＝ 1 秒。
 */
const SEEK_GRACE_TICKS = 4

/**
 * 播放器的位置與剛才要的秒數差多少之內算「seek 已經落地」。seek 落地後
 * `getCurrentTime()` 與目標只差幾十毫秒，還讀到好幾秒以外就是舊的位置。
 */
const SEEK_LANDED_SECS = 1

/** 清單底部的淡出高度，與 `mask-image` 的漸層同一個數字。 */
const FADE_PX = 64

/**
 * `idle` 還沒有人按播放（**首屏不載任何 YouTube 資源**）；`loading` script 在路上；
 * `ready` 播放器建好了；`failed` script 載不進來，只剩外連那條退路。
 */
type PlayerStatus = 'idle' | 'loading' | 'ready' | 'failed'

/**
 * 字幕示範：左邊影片＋字幕條，右邊逐句清單，兩邊同步。
 *
 * **整塊不畫任何框**：沒有卡片外框、沒有欄間線、沒有標頭底線。兩欄只靠留白分開，
 * 影片加字幕條是畫面上唯一的實體塊——框線一多，讀者的注意力就從「字幕長什麼樣」
 * 跑到格子上。
 *
 * 影片**原地播**：按下播放鈕才載 YouTube 的 script 並在封面的位置建播放器。這頁是
 * 行銷頁的首屏，一支還沒有人要看的影片不該讓它變慢，所以 mount 時什麼都不載。
 *
 * 「目前這句」有兩個來源：讀者點一句（順便 seek 影片），或播放中每 250ms 讀一次
 * `getCurrentTime()` 換算。兩個來源推同一個 `activeIndex`，字幕條與 `aria-current`
 * 都從它算出來——分開更新就會出現「字幕顯示第 3 句、影片停在第 1 句」這種只有盯著
 * 看才發現的錯。
 */
export function SubtitleDemo({ demo = null }: { demo?: SiteDemoReady | null }) {
  const { t } = useTranslation(['web'])
  const [activeIndex, setActiveIndex] = useState(0)
  const [status, setStatus] = useState<PlayerStatus>('idle')
  const markDemoStarted = useDemoStarted('subtitle')

  // 後端指定的示範（`site_demo.outreach_share_id`）優先；沒設定、撤銷、API 掛了
  // 都會拿到 null，這時用打包進來的那一份，示範區永遠有東西可看。
  const cues = useMemo(() => (demo ? toCues(demo) : SUBTITLE_CUES), [demo])
  const videoId = demo?.media.videoId ?? YOUTUBE_ID
  const poster = demo ? youtubePoster(demo.media.videoId) : VIDEO_POSTER

  const groups = useMemo(() => groupCuesBySpeaker(cues), [cues])
  const active = cues[activeIndex] ?? cues[0]

  const mountRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLOListElement>(null)
  const playerRef = useRef<YouTubePlayer | null>(null)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)
  /** tick 裡拿來比對的目前句；用 state 會把 interval 綁在某一次渲染的值上。 */
  const activeIndexRef = useRef(0)
  const startSecsRef = useRef(0)
  /**
   * 剛跳過去的那一句，以及還願意為它等幾個 tick。在播放器真的走到它之前，tick
   * 不把高亮往回推（見 `SEEK_GRACE_TICKS`）。
   */
  const pendingSeekRef = useRef<{ index: number; secs: number; ticksLeft: number } | null>(null)
  /** script 只請求一次：載入中再按不會開第二個播放器，失敗後也不重試。 */
  const requestedRef = useRef(false)
  const disposedRef = useRef(false)

  const stopTicking = useCallback(() => {
    if (tickRef.current) clearInterval(tickRef.current)
    tickRef.current = null
  }, [])

  const startTicking = useCallback(() => {
    stopTicking()
    tickRef.current = setInterval(() => {
      const player = playerRef.current
      if (!player) return
      // tick 只有一個職責：換句。先比對再 setState，不然每秒四次的 tick 會把 41 句
      // 的清單整份重畫，只為了一個沒有變的高亮。
      const secs = player.getCurrentTime()
      const next = cueIndexAt(cues, secs)
      const pending = pendingSeekRef.current
      if (pending) {
        // 「落地」要兩個條件都成立：位置就在剛才要的地方（往前往後跳都一樣），而且
        // 算出來的句子沒有落在剛點的那一句前面；起點有小數時，`start` 取整會讓影片
        // 從前一句的地盤上起跑，光比距離會誤判成已經到了。
        const landed = Math.abs(secs - pending.secs) <= SEEK_LANDED_SECS && next >= pending.index
        if (!landed && pending.ticksLeft > 0) {
          pending.ticksLeft -= 1
          return
        }
        pendingSeekRef.current = null
      }
      if (next === activeIndexRef.current) return
      activeIndexRef.current = next
      setActiveIndex(next)
    }, PLAYBACK_TICK_MS)
  }, [cues, stopTicking])

  useEffect(() => {
    disposedRef.current = false
    return () => {
      disposedRef.current = true
      stopTicking()
      playerRef.current?.destroy()
      playerRef.current = null
    }
  }, [stopTicking])

  /** 選一句 ＝ 從那一句播：播放器在就 seek，不在就載起來從那一句開始。 */
  const playFrom = useCallback(
    (index: number) => {
      markDemoStarted()
      const cue = cues[index]
      activeIndexRef.current = index
      startSecsRef.current = cue.start
      pendingSeekRef.current = { index, secs: cue.start, ticksLeft: SEEK_GRACE_TICKS }
      setActiveIndex(index)

      const player = playerRef.current
      if (player) {
        player.seekTo(cue.start, true)
        player.playVideo()
        return
      }
      if (requestedRef.current) return
      requestedRef.current = true
      setStatus('loading')
      void loadYouTubeApi()
        .then(() => {
          if (disposedRef.current || !mountRef.current) return
          playerRef.current = createYouTubePlayer(mountRef.current, {
            videoId,
            // privacy-enhanced：沒看影片就不會被種 cookie。
            host: 'https://www.youtube-nocookie.com',
            playerVars: {
              autoplay: 1,
              // 原生控制列留著。自動播放被瀏覽器擋下來時，讀者至少看得到怎麼播；
              // 想暫停、想拖進度也不必我們再刻一套。
              controls: 1,
              // 載入中被點掉的那幾句也算數：起播秒數認最後一次點的，不是第一次。
              // 往下取整：`start` 只吃整數，而且往前一點點比往後好，晚一秒起播會
              // 直接漏掉那一句的開頭。seek 與高亮用的仍是原本的浮點秒數。
              start: Math.floor(startSecsRef.current),
              playsinline: 1,
              rel: 0,
              modestbranding: 1,
              origin: window.location.origin,
            },
            events: {
              onReady: () => setStatus('ready'),
              // 時鐘只在影片真的在播的時候跑。`onReady` 不代表在播：autoplay 被
              // 瀏覽器擋下來時播放器就停在那裡，而每 250ms 問一次「播到哪了」的
              // 計時器會一路跑下去，什麼都不會變。
              onStateChange: (event) => {
                if (event.data === PLAYER_STATE.playing) startTicking()
                else if (event.data === PLAYER_STATE.ended || event.data === PLAYER_STATE.paused) {
                  stopTicking()
                }
              },
              onError: () => {
                stopTicking()
                setStatus('failed')
              },
            },
          })
        })
        .catch((caughtError) => {
          reportHandledException(caughtError, {
            source: 'features/transcription-page/subtitle-demo.tsx',
          })
          return setStatus('failed')
        })
    },
    [cues, startTicking, stopTicking, videoId, markDemoStarted]
  )

  // 跟著影片走的時候，目前這句要自己捲進視線內。只動清單自己的 scrollTop：
  // `scrollIntoView()` 會連整頁一起捲，而讀者正在看左邊那支影片。
  //
  // 「看得見」比清單的框小一圈：頂端有黏著的講者名會蓋住一列，底部 64px 是淡出區。
  // 用整個框去算，目前這句會停在半透明的地方或被人名蓋掉。
  useEffect(() => {
    const list = listRef.current
    const item = list?.querySelector<HTMLElement>(`[data-cue-index="${activeIndex}"]`)
    if (!list || !item) return
    const listBox = list.getBoundingClientRect()
    const itemBox = item.getBoundingClientRect()
    const stickyHeight = list.querySelector<HTMLElement>('[data-speaker-row]')?.offsetHeight ?? 0
    const visibleTop = listBox.top + stickyHeight
    const visibleBottom = listBox.bottom - FADE_PX
    if (visibleBottom <= visibleTop) return
    if (itemBox.top < visibleTop) list.scrollTop -= visibleTop - itemBox.top
    else if (itemBox.bottom > visibleBottom) list.scrollTop += itemBox.bottom - visibleBottom
  }, [activeIndex])

  const playLabel = t('web:transcriptionPage.subtitles.playFromAria', {
    time: formatCueClock(active.start),
  })
  const posterAlt = t('web:transcriptionPage.subtitles.posterAlt')

  return (
    <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-[clamp(24px,3.4vw,48px)] max-[860px]:grid-cols-1 max-[860px]:gap-0">
      <div className="min-w-0">
        {/* iframe 的尺寸寫在**這一層**，不是寫在掛載節點上：掛載節點會被 YouTube
            換掉，寫在它身上的 class 跟著消失，iframe 就會用預設的 640×360 站在這個
            `overflow-hidden` 的框裡，右邊與整條控制列都被裁掉。 */}
        <div className="relative aspect-video overflow-hidden rounded-t-[10px] bg-foreground [&_iframe]:absolute [&_iframe]:inset-0 [&_iframe]:size-full">
          {/* 這個節點會被**換成** iframe，所以它整個生命週期都留在 DOM 裡、不條件式
              渲染——React 不知道那個節點已經被換掉了，之後要移除它會炸。所有覆蓋層
              都排在它後面，才不會有人拿它當插入位置的參考點。 */}
          <div ref={mountRef} className="absolute inset-0" />
          {(status === 'idle' || status === 'loading') && (
            <button
              type="button"
              onClick={() => playFrom(activeIndex)}
              disabled={status === 'loading'}
              aria-label={playLabel}
              className="group absolute inset-0 block w-full bg-transparent p-0 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-background"
            >
              <Image
                src={poster}
                width={1280}
                height={720}
                alt={posterAlt}
                className="block size-full object-cover"
              />
              <span
                aria-hidden="true"
                className="absolute right-[14px] bottom-[14px] grid size-12 place-items-center rounded-full bg-background text-foreground shadow-[0_4px_14px_color-mix(in_oklch,var(--fg)_35%,transparent)] transition-transform group-hover:scale-[1.06] max-[860px]:right-[10px] max-[860px]:bottom-[10px] max-[860px]:size-10"
              >
                <Play className="ml-[3px] size-5 fill-current max-[860px]:size-4" strokeWidth={0} />
              </span>
            </button>
          )}
          {status === 'failed' && (
            // script 被擋掉、離線、逾時：留一塊黑框什麼都沒有是最糟的結果，
            // 至少把人送到 YouTube，而且帶著他選的那一句的秒數。
            <a
              href={youtubeUrlAt(videoId, active.start)}
              target="_blank"
              rel="noopener"
              className="absolute inset-0 block focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-background"
            >
              <Image
                src={poster}
                width={1280}
                height={720}
                alt={posterAlt}
                className="block size-full object-cover"
              />
              <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 bg-foreground/85 px-4 py-2.5 text-sm font-semibold text-background">
                <ExternalLink aria-hidden="true" className="size-4" />
                {t('web:transcriptionPage.subtitles.openOnYouTube')}
              </span>
            </a>
          )}
        </div>
        {/* 字幕條接在影片正下方、中間沒有縫：淡墨底配墨字，跟影片合成一個圓角外形，
            看得出這行字屬於這支影片。不用黑底——它會跟深色封面與 YouTube 播放器融成
            一塊，看不出這行字是 KIAO 產出的。
            不放「字幕預覽」文字標籤：左邊那枚 CC 圖示已經說完了，那四個字只講給
            螢幕閱讀器聽。 */}
        <div
          role="group"
          aria-label={t('web:transcriptionPage.subtitles.previewLabel')}
          className="relative grid min-h-[52px] place-items-center rounded-b-[10px] bg-fg-soft px-[60px] py-[10px] text-center text-foreground max-[860px]:min-h-[46px] max-[860px]:px-[44px] max-[860px]:py-[9px]"
        >
          <span
            aria-hidden="true"
            className="absolute top-1/2 left-4 -translate-y-1/2 rounded-[4px] border-[1.5px] border-current px-[5px] py-[2px] font-mono text-[10px] leading-[1.2] font-semibold tracking-[0.04em] text-muted max-[860px]:left-3"
          >
            CC
          </span>
          <span
            aria-live="polite"
            className="text-lg leading-[1.5] font-semibold max-[860px]:text-md"
          >
            {active.text}
          </span>
        </div>
      </div>

      <CueColumn groups={groups} activeIndex={activeIndex} onSelect={playFrom} listRef={listRef} />
    </div>
  )
}

/**
 * 逐句清單。**與左欄分開渲染**並 `memo`：41 句只有在目前這句真的換人時才需要重畫。
 *
 * 清單用 `grow basis-0` 而不是 `flex-1`：`flex-1` 是 `flex: 1 1 0%`，而**百分比**
 * 的 flex-basis 在容器高度未定時，於 intrinsic sizing 階段會退回內容高度——grid
 * 的列高於是被 41 句撐開，影片那一欄跟著被拉長，清單也就不再捲動。`basis-0` 是
 * 長度 0，列高才會由左欄決定。
 */
const CueColumn = memo(function CueColumn({
  groups,
  activeIndex,
  onSelect,
  listRef,
}: {
  groups: SpeakerGroup[]
  activeIndex: number
  onSelect: (index: number) => void
  listRef: React.RefObject<HTMLOListElement | null>
}) {
  const { t } = useTranslation(['web'])

  return (
    <div className="flex min-h-0 flex-col">
      <div className="flex items-baseline gap-3 pb-2 max-[860px]:pt-6">
        <p className="text-md font-semibold">
          {t('web:transcriptionPage.subtitles.transcriptLabel')}
          <span className="ml-2.5 text-sm font-normal text-muted">
            {t('web:transcriptionPage.subtitles.transcriptHint')}
          </span>
        </p>
      </div>
      {/* 底部 64px 淡出，提示下面還有；捲到底就把捲動交還整頁（所以**不設**
          `overscroll-behavior: contain`），讀者不會卡在清單裡。
          容器左右各外推 10px，選中那句的淡底比文字寬一圈，文字本身仍對齊標頭。 */}
      <ol
        ref={listRef}
        aria-label={t('web:transcriptionPage.subtitles.cuesAria')}
        className="mx-[-10px] min-h-0 grow basis-0 list-none overflow-y-auto pb-8 [mask-image:linear-gradient(black_calc(100%_-_64px),transparent)] max-[860px]:max-h-[320px] max-[860px]:grow-0 max-[860px]:basis-auto"
      >
        {groups.map((group) => (
          <React.Fragment key={`${group.name ?? ''}-${group.cues[0].index}`}>
            {/* 講者名只在換人時標一次，捲動時黏在清單頂端，下一位講者捲上來就接手。
                它是清單的分隔標，不是可點的項目，所以對螢幕閱讀器隱藏——每一句的
                aria-label 已經帶了講者名。
                逐字稿沒有講者（沒跑分離）就整列不畫，清單只剩句子。 */}
            {group.name !== null && (
              <li
                aria-hidden="true"
                data-speaker-row
                className={cn(
                  'sticky top-0 z-[1] bg-background px-[10px] pt-3 pb-1 text-xs font-semibold',
                  group.order === 1 ? 'text-foreground' : 'text-muted'
                )}
              >
                {group.name}
              </li>
            )}
            {group.cues.map((cue) => {
              const current = cue.index === activeIndex
              return (
                <li key={cue.index}>
                  <button
                    type="button"
                    data-cue-index={cue.index}
                    aria-current={current ? 'true' : undefined}
                    aria-label={cueLabel(cue)}
                    onClick={() => onSelect(cue.index)}
                    className={cn(
                      'grid w-full grid-cols-[44px_minmax(0,1fr)] items-baseline gap-2.5 rounded-md bg-transparent px-[10px] py-[7px] text-left transition-colors hover:bg-fg-soft focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foreground max-[860px]:grid-cols-[40px_minmax(0,1fr)] max-[860px]:py-2',
                      current && 'bg-fg-soft'
                    )}
                  >
                    <time
                      className={cn(
                        'font-mono text-2xs [font-variant-numeric:tabular-nums]',
                        current ? 'font-semibold text-foreground' : 'text-muted'
                      )}
                    >
                      {formatCueClock(cue.start)}
                    </time>
                    <span className={cn('text-md leading-[1.6]', current && 'font-semibold')}>
                      {cue.text}
                    </span>
                  </button>
                </li>
              )
            })}
          </React.Fragment>
        ))}
      </ol>
    </div>
  )
})
